import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { scrollToSection } from '../utils/scrollUtils';

interface HeroProps {
  t: any;
  isRTL: boolean;
  language: 'fr' | 'en' | 'ar';
}

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const SliderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  touch-action: pan-y; /* Allow vertical scrolling but handle horizontal swipes */
`;

const Slide = styled(motion.div)<{ $bgImage: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${props => props.$bgImage}) center/cover no-repeat;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  object-fit: cover;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }
`;

const HeroContent = styled.div<{ $isRTL: boolean }>`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const LogoContainer = styled(motion.div)`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 0.8rem;
`;

const Logo = styled.img`
  height: 180px;
  width: auto;
  filter: brightness(0) invert(1); /* Makes the logo white */
  
  @media (max-width: 768px) {
    height: 120px;
  }
`;

const TextLogo = styled.img`
  height: 60px;
  width: auto;
  filter: brightness(0) invert(1); /* Makes the text logo white */
  
  @media (max-width: 768px) {
    height: 40px;
  }
`;

const Description = styled(motion.p)<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const CTAButton = styled(motion.button)<{ $isRTL: boolean }>`
  background: linear-gradient(135deg, #0f172a 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(220, 38, 38, 0.3);
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }
`;

const BottomControlsContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  z-index: 3;
`;

const DotsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.div<{ $isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$isActive ? '#dc2626' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$isActive ? '#dc2626' : 'rgba(255, 255, 255, 0.8)'};
  }
`;

const ScrollIndicator = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: white;
  cursor: pointer;
`;

const ScrollText = styled.span`
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const ScrollArrow = styled(motion.div)`
  width: 2px;
  height: 20px;
  background: white;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: -3px;
    width: 8px;
    height: 8px;
    border-right: 2px solid white;
    border-bottom: 2px solid white;
    transform: rotate(45deg);
  }
`;

const Hero: React.FC<HeroProps> = ({ t, isRTL, language }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const slides = [
'/images/im00.jpg',
'/images/im0.jpg',
'/images/im1.png',
'/images/im4.jpg',
    '/images/im90.png',
'/images/im5.png',
'/images/im3.png'
  ];

  // Auto-advance slides
  useEffect(() => {
    if (!isAutoPlay || isTransitioning) return;

    intervalRef.current = setInterval(() => {
      setDirection('left');
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlay, isTransitioning, slides.length]);

  // Handle transition state
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  const goToSlide = (index: number) => {
    if (index === currentSlide || isTransitioning) return;
    
    setDirection(index > currentSlide ? 'left' : 'right');
    setCurrentSlide(index);
    setIsAutoPlay(false);
    
    // Resume auto-play after 10 seconds
    setTimeout(() => {
      setIsAutoPlay(true);
    }, 10000);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      setDirection('left');
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    } else if (isRightSwipe) {
      setDirection('right');
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
    
    setIsAutoPlay(false);
    setTimeout(() => {
      setIsAutoPlay(true);
    }, 10000);
  };

  const handleMouseEnter = () => {
    setIsAutoPlay(false);
  };

  const handleMouseLeave = () => {
    setIsAutoPlay(true);
  };

  const slideVariants = {
    enter: (direction: 'left' | 'right' | null) => ({
      x: direction === 'left' ? '100%' : direction === 'right' ? '-100%' : 0,
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: 'left' | 'right' | null) => ({
      zIndex: 0,
      x: direction === 'left' ? '-100%' : direction === 'right' ? '100%' : 0,
      opacity: 0,
    }),
  };

  const handleCTAClick = () => {
    scrollToSection('services');
  };

  const handleScrollClick = () => {
    console.log('Scroll click handler called');
    scrollToSection('about');
  };

  return (
    <HeroSection id="home"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SliderContainer
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <Slide
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            $bgImage={slides[currentSlide]}
          />
        </AnimatePresence>
      </SliderContainer>

      <HeroContent $isRTL={isRTL}>
        <LogoContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Logo src="/logo192.png" alt="TECHYID" />
          <TextLogo src={language === 'ar' ? "/images/textar.png" : "/images/text.png"} alt="TECHYID" />
        </LogoContainer>
        
        
        <CTAButton
          $isRTL={isRTL}
          onClick={handleCTAClick}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t.ctaButton}
        </CTAButton>
      </HeroContent>

      <BottomControlsContainer>
        <DotsContainer>
          {slides.map((_, index) => (
            <Dot
              key={index}
              $isActive={currentSlide === index}
              onClick={() => goToSlide(index)}
            />
          ))}
        </DotsContainer>

        <ScrollIndicator
          onClick={handleScrollClick}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          whileHover={{ y: 5 }}
        >
          <ScrollText>Défiler vers le bas</ScrollText>
          <ScrollArrow
            animate={{ y: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </ScrollIndicator>
      </BottomControlsContainer>
    </HeroSection>
  );
};

export default Hero;
